/* Copyright (c) 2020-present CloudIO Inc */

import { IconButton } from '@mui/material';
import copy from 'copy-to-clipboard';
import { UserSession } from 'root/useUser';
import { Platform } from '../componentregistry/platform/Platform';
import { getBaseUrl, getUrl } from '../config/config';
import Icon from '../icons/Icon';
import { isNull } from './isEmpty';
import React from 'react';

let reload = false;

export function reloadPage() {
  reload = true;
  window.location.reload();
}

export function isReload() {
  return reload;
}

export function useQueryString(platform: Platform) {
  const navLoc = platform.navLoc;
  if (!navLoc) {
    platform.showError('useQueryString: navLoc is not available!');
    return { query: '', pathname: '' };
  }
  // this is the latest location
  const { search, hash, pathname } = navLoc.location;
  let _index = search.indexOf('?');
  let query = search;
  if (_index !== -1) {
    query = search.substring(_index);
  } else {
    _index = hash.indexOf('?');
    if (_index !== -1) {
      query = hash.substring(_index);
    }
  }
  return { query, pathname };
}

function _getURLParam(key: string): string | null {
  // eslint-disable-next-line no-restricted-globals
  const hash = location.hash;
  const _index = hash.indexOf('?');
  if (_index !== -1) {
    const query = hash.substring(_index);
    const searchParams = new URLSearchParams(query);
    return searchParams.get(key);
  }
  return null;
}

export function getURLParam(key: string): string | null {
  const value = _getURLParam(key);
  if (isNull(value)) {
    let params = _getURLParam('_wx');
    if (params) {
      const p = JSON.parse(atob(params));
      return p[key];
    }
    params = _getURLParam('_p');
    if (params) {
      const p = JSON.parse(atob(params));
      return p[key];
    }
  }
  return value;
}

export function isFromWF(): boolean {
  return !!_getURLParam('_wx');
}

export function getPageParams(): string | null {
  return _getURLParam('_p');
}

function getURLQueryParam(key: string): string | null {
  // eslint-disable-next-line no-restricted-globals
  const search = location.search;
  const _index = search.indexOf('?');
  if (_index !== -1) {
    const query = search.substr(_index);
    const searchParams = new URLSearchParams(query);
    return searchParams.get(key);
  }
  return null;
}

export function getURLQueryStringToPost(user?: UserSession) {
  const _h = getURLQueryParam('_h');
  if (!user || user.x === 'GUEST') {
    return _h ? `_h=${_h}` : '';
  }
  return _h
    ? `x=${encodeURIComponent(user.x)}&_h=${_h}`
    : `x=${encodeURIComponent(user.x)}`;
}

export async function tinyUrlFor({
  platform,
  user,
  appUid,
  defaultName,
  url,
}: {
  platform: Platform;
  user: UserSession;
  appUid: string;
  defaultName?: string;
  url: string;
}) {
  let name = '';
  try {
    name = await platform.userInput({
      title: 'Friendly Name',
      placeholder: 'e.g. my-friendly-name',
      label: 'Friendly name for the link',
      content: `If provided, it will be used as part of the link e.g. ${getBaseUrl()}go/<friendly-name>. Leave blank to use a random string.`,
      required: false,
      defaultName,
      isValid: (value) => encodeURIComponent(value) === value,
      onChange: (value) => value.toLocaleLowerCase(),
    });
  } catch (e) {
    // User cancelled the input
    return;
  }

  const response = await fetch(
    user.isAuthenticated
      ? `${getUrl()}tiny_url?${getURLQueryStringToPost(user)}`
      : `${getUrl()}tiny_url_pub`,
    {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-application': appUid,
      },
      // eslint-disable-next-line no-restricted-globals
      body: JSON.stringify({ url, name }),
    },
  );

  const json = await response.json();
  if (json.status === 'OK') {
    copy(`${getBaseUrl()}go/${json.uid}`, {
      debug: false,
      format: 'text/plain',
    });
    platform.showSuccess(
      `URL copied to clipboard: ${getBaseUrl()}go/${json.uid}`,
    );
  } else {
    platform.showError(json.message);
    await tinyUrlFor({ platform, user, appUid, defaultName: name, url });
  }
}

export async function tinyUrl({
  platform,
  user,
  appUid,
  defaultName,
}: {
  platform: Platform;
  user: UserSession;
  appUid: string;
  defaultName?: string;
}) {
  // eslint-disable-next-line no-restricted-globals
  const url = location.href;
  return tinyUrlFor({ platform, user, appUid, defaultName, url });
}

export function ShareUrl({
  platform,
  user,
  appUid,
}: {
  platform: Platform;
  user: UserSession;
  appUid: string;
}) {
  return (
    <IconButton
      data-testid="share-url"
      color="primary"
      onClick={async () => {
        await tinyUrl({ platform, user, appUid });
      }}
      data-tip="Share URL"
      aria-label="Share URL"
    >
      <span aria-hidden="false" style={{ display: 'none' }}>
        Share URL
      </span>
      <Icon iconName="share" />
    </IconButton>
  );
}
